import { useContext, useState, useRef } from "react";
import {
  Flex,
  VStack,
  HStack,
  Input,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  InputGroup,
  InputRightElement,
  StackDivider,
  Spinner,
  Text,
  Box,
  useToast,
  Center,
  Fade,
  AccordionPanel,
  AccordionButton,
  AccordionItem,
  Accordion,
} from "@chakra-ui/react";
import {
  faEye,
  faEyeSlash,
  faAt,
  faUser,
  faGear,
  faArrowRightFromBracket,
  faInfo,
  faQuestion,
  faUserAlt,
  faICursor,
  faXmarkCircle,
  faPlus,
  faSitemap,
  faCheck,
  faEnvelope,
  faGlobe,
  faHardDrive,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as FA } from "@fortawesome/react-fontawesome";
import Button from "../new_component/atoms/Button";
import styled from "styled-components";
import DefaultLayout from "../layouts/default";
import { GlobalContext } from "../component/GlobalContext";
import { useLocation, useParams } from "react-router-dom";
import { activationURL, apiBaseURL, hostBaseURL } from "../config";
import Cookies from "js-cookie";
import emailjs from "emailjs-com";
import { normalizePropName } from "../utils/normalization";

const MainContent = ({ children }) => (
  <Flex justify="center" mt={"20%"}>
    {children}
  </Flex>
);

const Header = styled.h3`
  font-size: 28px;
  font-weight: 700;
`;

const UserInfoItem = ({ children, fn }) => {
  return (
    <Flex
      justify={"left"}
      align={"center"}
      gap={4}
      cursor={"pointer"}
      width={"100%"}
      _hover={{
        backgroundColor: "brand.100",
        padding: "2%",
        borderRadius: "10px",
        transition: "all 0.2s ease",
      }}
      onClick={fn}
    >
      {children}
    </Flex>
  );
};

const OrganizationsList = () => {
  const toast = useToast();
  const { organizations, setOrganizations, runCode, setShowLogin, userInfo } =
    useContext(GlobalContext);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const inputRef = useRef();

  const handleSetOrganization = (org) => {
    if (org["IS-ACTIVE"] === "true") {
      setShowLogin(false);
    } else {
      runCode({
        code: `set organization "${org.ORGANIZATION}"`,
        token: userInfo.token,
      })
        .then((data) => {
          if (!data.error) {
            window.location = "/";
          } else {
            toast({
              title: "Error",
              description: data.msg,
              status: "error",
              duration: 3000,
              isClosable: true,
              position: "bottom-right",
            });
          }
        })
        .catch((err) => {
          toast({
            title: "Error",
            description: err.toString(),
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
        });
    }
  };

  const handleSetOrgName = (e) => {
    setOrgName(e.target.value);
  };

  const getOrgName = (org) => {
    const orgOwner = org["OWNER-NICKNAME"];
    const lastChar = orgOwner[orgOwner?.length - 1];
    const name = org.ORGANIZATION;
    if (name === "main") {
      if (lastChar === "s") {
        return `${orgOwner[0].toUpperCase()}${org["OWNER-NICKNAME"].substring(
          1
        )}' Main organization`;
      }
      return `${orgOwner[0].toUpperCase()}${org["OWNER-NICKNAME"].substring(
        1
      )}'s Main organization`;
    } else return `${name[0].toUpperCase()}${name.substring(1)}`;
  };

  const handleCreateOrg = () => {
    if (orgName?.trim() === "") {
      toast({
        title: "Error",
        description: "Insert an organization name",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    if (orgName.includes("'") || orgName.includes('"')) {
      toast({
        title: "Error",
        description: "Organization name must not include ' or \"",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    setLoading(true);

    runCode({ code: `create organization "${orgName}"`, token: userInfo.token })
      .then((data) => {
        if (!data.error) {
          setOrganizations([
            ...organizations,
            {
              "OWNER-NICKNAME": `${userInfo.nickname}`,
              ORGANIZATION: `${orgName}`,
              "IS-ACTIVE": "false",
            },
          ]);
          setLoading(false);
          setCreating(false);
          setOrgName("");
        } else {
          setLoading(false);
          toast({
            title: `${!data.error ? "Success" : "Error"}`,
            description: data.msg,
            status: `${!data.error ? "success" : "error"}`,
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        toast({
          title: "Error",
          description: `Failed to send email! Check your connection and try again. ${err}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape" && creating) {
      e.preventDefault();
      setCreating(false);
      setOrgName("");
    }
    if (e.key === "Enter") {
      handleCreateOrg();
    }
  };

  const handleClearInput = () => {
    inputRef.current?.focus();
    setOrgName("");
  };

  const handleSetCreating = () => {
    setCreating(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  return (
    <Accordion allowMultiple width={"100%"}>
      <AccordionItem border={"none"}>
        <AccordionButton p={0} _hover={{}}>
          <Box mr={4}>
            <FA icon={faSitemap} />
          </Box>
          Organizations
        </AccordionButton>
        <AccordionPanel pb={0}>
          {organizations?.map((org) => (
            <HStack gap={0.5} key={`org-container-${org.ORGANIZATION}`}>
              {org["IS-ACTIVE"] === "true" && <FA icon={faCheck} />}
              <Box
                width={"100%"}
                _hover={{
                  backgroundColor: "brand.200",
                }}
                m={1}
                p={1}
                borderRadius={"md"}
                onClick={() => handleSetOrganization(org)}
              >
                {getOrgName(org)}
              </Box>
            </HStack>
          ))}
          {!creating && (
            <Fade in={!creating}>
              <HStack
                gap={0.5}
                _hover={{
                  backgroundColor: "brand.200",
                }}
                borderRadius={"md"}
                pb={0}
                pt={0}
                p={1}
                onClick={handleSetCreating}
              >
                <FA icon={faPlus} />
                <Box width={"100%"} p={1}>
                  Add new organization
                </Box>
              </HStack>
            </Fade>
          )}
          {creating && !loading && (
            <Fade in={creating}>
              <HStack gap={4}>
                <InputGroup
                  width={"95%"}
                  variant={"flushed"}
                  bgColor={"transparent"}
                >
                  <Input
                    ref={inputRef}
                    type={"text"}
                    value={orgName}
                    placeholder="Organization name"
                    onChange={handleSetOrgName}
                    disabled={loading}
                    onKeyDown={handleKeyDown}
                    _placeholder={{ textAlign: "center" }}
                    textAlign={"center"}
                  />
                  <InputRightElement>
                    <FA
                      icon={faXmarkCircle}
                      color={"gray"}
                      onClick={handleClearInput}
                      fontSize={"12px"}
                    />
                  </InputRightElement>
                </InputGroup>
              </HStack>
            </Fade>
          )}
          {loading && (
            <Center>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="brand.500"
                size="sm"
              />
            </Center>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const RecoverPassword = ({ setRecovering }) => {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [recovered, setRecovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleSetRecovery = () => {
    if (!loading) {
      setRecovering(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    if (email === "") {
      toast({
        title: "Error",
        description: `Fill out the blank spaces`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    setLoading(true);
    e.target.disabled = true;

    await fetch(`${apiBaseURL}/password_reset/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.msg === "New password request") {
          const activation_token = data.token;
          emailjs
            .send(
              "service_mbe6mac",
              "template_m7t17n9",
              {
                to_email: email,
                token: activation_token,
                host: hostBaseURL,
              },
              "JtJkEdwYagbvyGlcX"
            )
            .then((response) => {
              setLoading(false);
              e.target.disabled = false;
              setRecovered(true);
              console.log(
                "MAILED SUCCESSFULLY!",
                response.status,
                response.text
              );
            })
            .catch((err) => {
              setLoading(false);
              e.target.disabled = false;
              toast({
                title: "Error",
                description: `Failed to send email! Check your connection and try again. ${err.toString()}`,
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "bottom-right",
              });
            });
        } else {
          toast({
            title: `${!data.error ? "Success" : "Error"}`,
            description: data.msg,
            status: `${!data.error ? "success" : "error"}`,
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
          setLoading(false);
          e.target.disabled = false;
        }
      })
      .catch((err) => {
        toast({
          title: "Error",
          description: `Failed to connect to backend engine. ${err.toString()}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
        setLoading(false);
        e.target.disabled = false;
      });
  };

  const handleKeyDown = (e) => {
    const recoverBtn = document.getElementById("recover-btn");
    if (e.key === "Enter") recoverBtn.click();
  };

  return recovered ? (
    <VStack w={"100%"} gap={4}>
      <Header>Request Sent!</Header>
      <VStack w="100%" gap={5}>
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "550",
            textAlign: "center",
          }}
        >
          A password reset request has been sent to your email.
        </h3>
        <Header>✅</Header>
        <h3
          style={{
            fontSize: "15px",
            fontWeight: "550",
            textAlign: "center",
          }}
        >
          Check your inbox and follow the instructions.
        </h3>
      </VStack>
    </VStack>
  ) : (
    <>
      <Header>Recover Password</Header>
      <VStack w="100%" gap={5}>
        <InputGroup>
          <Input
            placeholder="E-mail address"
            bgColor="bg"
            onChange={handleChangeEmail}
            disabled={loading}
            onKeyDown={handleKeyDown}
          />
          <InputRightElement>
            <FA icon={faAt} />
          </InputRightElement>
        </InputGroup>
      </VStack>
      <HStack gap="20px">
        <Button onClick={handleRecover} id="recover-btn">
          {loading ? (
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="md"
            />
          ) : (
            "Continue"
          )}
        </Button>
        <Button variant="link" onClick={handleSetRecovery}>
          Try to remember?
        </Button>
      </HStack>
    </>
  );
};

export const ResetPassword = () => {
  const toast = useToast();
  const { activation_token } = useParams();
  const [reseting, setReseting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [reset, setReset] = useState(false);

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleRedirect = () => {
    setTimeout(() => {
      handleGoHome();
    }, 5000);
  };

  const togglePasswordLogin = () => {
    setShowPassword(!showPassword);
  };

  const handlePassword = (event) => {
    setPassword(event.target.value);
  };

  const handleConfirmPassword = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleActivateAccount = async (e) => {
    e.preventDefault();
    if (password === "" || confirmPassword === "") {
      toast({
        title: "Error",
        description: `Fill out the blank spaces`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    setReseting(true);
    e.target.disabled = true;
    if (password === confirmPassword) {
      fetch(`${apiBaseURL}/password_reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: activation_token.toString(),
          new_password: password.toString(),
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.msg === "Password changed successfully") {
            setReseting(false);
            e.target.disabled = false;
            setReset(true);
            handleRedirect();
          } else {
            setReseting(false);
            e.target.disabled = false;
            toast({
              title: `${!data.error ? "Success" : "Error"}`,
              description: data.msg,
              status: `${!data.error ? "success" : "error"}`,
              duration: 3000,
              isClosable: true,
              position: "bottom-right",
            });
          }
        })
        .catch((err) => {
          setReseting(false);
          e.target.disabled = false;
          toast({
            title: "Error",
            description: `Failed to connect to backend engine. ${err.toString()}`,
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
        });
    } else {
      setReseting(false);
      e.target.disabled = false;
      toast({
        title: "Error",
        description: "The passwords don't match. Try again!",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  const handleKeyDown = (e) => {
    const resetBtn = document.getElementById("reset-btn");
    if (e.key === "Enter") resetBtn.click();
  };

  return (
    <DefaultLayout>
      <Flex justify="center" mt={"5%"}>
        <VStack
          justify="space-around"
          minH="350px"
          m="0 auto"
          p="30px 50px"
          bgColor="brand.100"
          borderRadius="24px"
        >
          <Header>{reset ? "Password reset!" : "Reset Password"}</Header>
          {reset ? (
            <>
              <Text as="h1">The password has been reset</Text>
              <Text as="h3" align={"center"}>
                You can now log in to your account
              </Text>
              <Button variant="link" onClick={handleGoHome}>
                Homepage
              </Button>
              <Text as="h3" align={"center"}>
                {`You will be redirected to home page in 5 seconds...`}
              </Text>
            </>
          ) : (
            <>
              <VStack gap={5}>
                <InputGroup>
                  <Input
                    type={`${showPassword ? "text" : "password"}`}
                    placeholder="New password"
                    bgColor="bg"
                    onChange={handlePassword}
                    disabled={reseting}
                    onKeyDown={handleKeyDown}
                  />
                  <InputRightElement>
                    <FA
                      icon={showPassword ? faEyeSlash : faEye}
                      onClick={togglePasswordLogin}
                    />
                  </InputRightElement>
                </InputGroup>
                <InputGroup>
                  <Input
                    type={`${showPassword ? "text" : "password"}`}
                    placeholder="Confirm password"
                    bgColor="bg"
                    onChange={handleConfirmPassword}
                    disabled={reseting}
                    onKeyDown={handleKeyDown}
                  />
                  <InputRightElement>
                    <FA
                      icon={showPassword ? faEyeSlash : faEye}
                      onClick={togglePasswordLogin}
                    />
                  </InputRightElement>
                </InputGroup>
              </VStack>
              <HStack gap="20px">
                <Button onClick={handleActivateAccount} id="reset-btn">
                  {reseting ? (
                    <Spinner
                      thickness="4px"
                      speed="0.65s"
                      emptyColor="gray.200"
                      color="brand.500"
                      size="md"
                    />
                  ) : (
                    "Continue"
                  )}
                </Button>
                <Button variant="link" href="/">
                  Remembered password?
                </Button>
              </HStack>
            </>
          )}
        </VStack>
      </Flex>
    </DefaultLayout>
  );
};

const UserProfile = () => {
  const { userInfo } = useContext(GlobalContext);

  return (
    <Accordion allowMultiple width={"100%"}>
      <AccordionItem border={"none"}>
        <AccordionButton p={0} _hover={{}}>
          <Box mr={4}>
            <FA icon={faUserAlt} />
          </Box>
          Your Profile
        </AccordionButton>
        <AccordionPanel pb={0}>
          <HStack gap={0.5}>
            <FA icon={faAt} />
            <Box width={"100%"} m={1} p={1} borderRadius={"md"}>
              {userInfo.nickname}
            </Box>
          </HStack>
          <HStack gap={0.5}>
            <FA icon={faEnvelope} />
            <Box width={"100%"} m={1} p={1} borderRadius={"md"}>
              {userInfo.email}
            </Box>
          </HStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const Settings = () => {
  return (
    <Accordion allowMultiple width={"100%"}>
      <AccordionItem border={"none"}>
        <AccordionButton p={0} _hover={{}}>
          <Box mr={4}>
            <FA icon={faGear} />
          </Box>
          Settings
        </AccordionButton>
        <AccordionPanel pb={0}>
          <HStack gap={0.5}>
            <FA icon={faGlobe} />
            <Box width={"100%"} m={1} p={1} borderRadius={"md"}>
              English
            </Box>
          </HStack>
          <HStack gap={0.5}>
            <FA icon={faHardDrive} />
            <Box width={"100%"} m={1} p={1} borderRadius={"md"}>
              1 GB
            </Box>
          </HStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const LoggedIn = ({ userInfo, doLogout }) => {
  const openSynchPage = () => {
    window.open("https://synchronit.com", "_blank").focus();
  };

  const goToFAQs = () => {
    window.open("/faqs");
  };
  return (
    <VStack width={"80%"} alignSelf={"flex-start"}>
      <Flex justify={"center"} align={"center"} gap={4} direction={"column"}>
        <Flex
          border={"4px solid black"}
          borderRadius={"full"}
          width={"75px"}
          height={"75px"}
          align={"center"}
          justify={"center"}
        >
          <FA icon={faUser} fontSize={"2em"} />
        </Flex>
        Welcome back, {normalizePropName(userInfo.nickname)}
      </Flex>
      <VStack
        divider={<StackDivider />}
        align={"flex-start"}
        mt={10}
        width={"100%"}
      >
        <UserInfoItem>
          <UserProfile />
        </UserInfoItem>
        <UserInfoItem>
          <OrganizationsList />
        </UserInfoItem>
        <UserInfoItem>
          <Settings />
        </UserInfoItem>
        <UserInfoItem fn={goToFAQs}>
          <FA icon={faQuestion} />
          F.A.Q.
        </UserInfoItem>
        <UserInfoItem fn={openSynchPage}>
          <FA icon={faInfo} />
          About us
        </UserInfoItem>
        <UserInfoItem fn={doLogout}>
          <FA icon={faArrowRightFromBracket} />
          Logout
        </UserInfoItem>
      </VStack>
    </VStack>
  );
};

const SignupForm = ({ setLogin }) => {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  const handleSetLogin = () => {
    setLogin(true);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (email === "") {
      toast({
        title: "Error",
        description: `Fill out the blank spaces`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    e.target.disabled = true;
    setSigning(true);
    await fetch(`${apiBaseURL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, activationURL }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) {
          setSigning(false);
          setSigned(true);
        } else {
          toast({
            title: `${!data.error ? "Success" : "Error"}`,
            description: data.error,
            status: `${!data.error ? "success" : "error"}`,
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
          setSigning(false);
          e.target.disabled = false;
        }
      })
      .catch((err) => {
        toast({
          title: "Error",
          description: `Failed to connect to backend engine. ${err.toString()}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
        setSigning(false);
        e.target.disabled = false;
      });
  };

  const handleKeyDown = (e) => {
    const signBtn = document.getElementById("sign-in");
    if (e.key === "Enter") signBtn.click();
  };

  return (
    <VStack
      justify="space-around"
      w="80%"
      minH="350px"
      m="0 auto"
      p="30px 50px"
      bgColor="brand.100"
      borderRadius="24px"
    >
      <Header>{signed ? "You've signed up" : "Sign up!"}</Header>
      {signed ? (
        <>
          <Text as="h1">Thanks for registering!</Text>
          <Text as="h3" align={"center"}>
            Verify your email inbox to activate your account!
          </Text>
          <Button variant="link" onClick={handleSetLogin}>
            Already have an account?
          </Button>
        </>
      ) : (
        <>
          <InputGroup>
            <Input
              placeholder="E-mail address"
              bgColor="bg"
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              disabled={signing}
            />
            <InputRightElement>
              <FA icon={faAt} />
            </InputRightElement>
          </InputGroup>
          <HStack gap="20px">
            <Button onClick={handleSignUp} id="sign-in">
              {signing ? (
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="brand.500"
                  size="md"
                />
              ) : (
                "Continue"
              )}
            </Button>
            <Button variant="link" onClick={handleSetLogin}>
              Already have an account?
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
};

const LoginForm = ({ setSignUp }) => {
  const toast = useToast();
  const { setUserInfo, setLoggedIn } = useContext(GlobalContext);
  const [showPassword, setShowPassword] = useState(false);
  const [logging, setLogging] = useState(false);
  const [email, setEmail] = useState("");
  const [passwordLogin, setPasswordLogin] = useState("");
  const [recovering, setRecovering] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSetSignUp = () => {
    setSignUp(false);
  };

  const handleChangeEmail = (e) => {
    setEmail(e.target.value);
  };

  const handleSetPassword = (e) => {
    setPasswordLogin(e.target.value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (email === "" || passwordLogin === "") {
      toast({
        title: "Error",
        description: `Fill out the blank spaces`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    setLogging(true);
    e.target.disabled = true;

    await fetch(`${apiBaseURL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        password: passwordLogin,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) {
          setUserInfo({
            email: email.toString(),
            token: data.token,
            loggedIn: true,
            nickname: data.user.nickname,
          });
          setLoggedIn(true);
          Cookies.set(
            "userInfo",
            JSON.stringify({
              email: email.toString(),
              token: data.token,
              loggedIn: true,
              nickname: data.user.nickname,
            }),
            {
              expires: 1 / 24,
              secure: !apiBaseURL.includes("localhost"),
              sameSite: "strict",
            }
          );
          setTimeout(() => (window.location = "/"), 50);
        } else {
          toast({
            title: "Error",
            description:
              "Logging error.\nVerify your username and password and try again!",
            status: `error`,
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
          setLogging(false);
          e.target.disabled = false;
        }
      })
      .catch((err) => {
        setLogging(false);
        e.target.disabled = false;
        toast({
          title: "Error",
          description: err.toString(),
          status: `error`,
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
      });
  };

  const handleSetRecovering = (e) => {
    e.preventDefault();
    if (!logging) {
      setRecovering(true);
    }
  };

  const handleKeyDown = (e) => {
    const loginBtn = document.getElementById("login-btn");
    if (e.key === "Enter") loginBtn.click();
  };

  return (
    <VStack
      justify="space-around"
      w="80%"
      minH="350px"
      m="0 auto"
      p="30px 50px"
      bgColor="brand.100"
      borderRadius="24px"
    >
      {recovering ? (
        <RecoverPassword setRecovering={setRecovering} />
      ) : (
        <>
          <Header>Login!</Header>
          <VStack w="100%" gap={5}>
            <InputGroup>
              <Input
                placeholder="E-mail address"
                bgColor="bg"
                onChange={handleChangeEmail}
                disabled={logging}
                onKeyDown={handleKeyDown}
              />
              <InputRightElement>
                <FA icon={faAt} />
              </InputRightElement>
            </InputGroup>
            <VStack gap={0} width={"100%"}>
              <InputGroup>
                <Input
                  type={`${showPassword ? "text" : "password"}`}
                  placeholder="Password"
                  bgColor="bg"
                  onChange={handleSetPassword}
                  disabled={logging}
                  onKeyDown={handleKeyDown}
                />
                <InputRightElement>
                  <FA
                    icon={showPassword ? faEyeSlash : faEye}
                    onClick={toggleShowPassword}
                  />
                </InputRightElement>
              </InputGroup>
              <Flex width={"100%"} justify={"flex-end"} pr={"10px"}>
                <Box
                  cursor={"pointer"}
                  fontSize={"14px"}
                  color={"brand.500"}
                  _hover={{ fontWeight: "bold" }}
                  onClick={handleSetRecovering}
                >
                  Forgot password?
                </Box>
              </Flex>
            </VStack>
          </VStack>
          <HStack gap="20px">
            <Button onClick={handleLogin} id="login-btn">
              {logging ? (
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="brand.500"
                  size="md"
                />
              ) : (
                "Continue"
              )}
            </Button>
            <Button variant="link" onClick={handleSetSignUp}>
              Don't have an account?
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
};

export const ActivateEmail = () => {
  const toast = useToast();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const [activating, setActivating] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [activated, setActivated] = useState(false);
  const [nickname, setNickname] = useState("");
  
  const handleRedirect = () => {
    setTimeout(() => {
      handleGoHome();
    }, 5000);
  };
  const handleGoHome = () => {
    window.location.href = "/";
  };

  const togglePasswordLogin = () => {
    setShowPassword(!showPassword);
  };

  const handlePassword = (event) => {
    setPassword(event.target.value);
  };

  const handleConfirmPassword = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleActivateAccount = async (e) => {
    e.preventDefault();
    if (password === "" || confirmPassword === "") {
      toast({
        title: "Error",
        description: `Fill out the blank spaces`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    setActivating(true);
    e.target.disabled = true;
    if (password === confirmPassword) {
      fetch(`${apiBaseURL}/auth/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token.toString(),
          nickname: nickname,
          password: password.toString(),
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data.error) {
            setActivating(false);
            setActivated(true);
            handleRedirect();
          } else {
            toast({
              title: `${!data.error ? "Success" : "Error"}`,
              description: data.error,
              status: `${!data.error ? "success" : "error"}`,
              duration: 3000,
              isClosable: true,
              position: "bottom-right",
            });
          }
        })
        .catch((err) => {
          toast({
            title: "Error",
            description: `Failed to connect to backend engine. ${err.toString()}`,
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "bottom-right",
          });
        });
      setActivated(true);
    } else {
      setActivating(false);
      e.target.disabled = false;
      toast({
        title: "Error",
        description: "The passwords don't match. Try again!",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  const handleSetNickname = (e) => {
    setNickname(e.target.value);
  };

  const handleKeyDown = (e) => {
    const activateBtn = document.getElementById("activate-btn");
    if (e.key === "Enter") activateBtn.click();
  };

  return (
    <DefaultLayout>
      <Flex justify="center" mt={"5%"}>
        <VStack
          justify="space-around"
          minH="350px"
          m="0 auto"
          p="30px 50px"
          bgColor="brand.100"
          borderRadius="24px"
        >
          <Header>
            {activated ? "Account activated!" : "Activate Account!"}
          </Header>
          {activated ? (
            <>
              <Text as="h1">This account has been activated</Text>
              <Text as="h3" align={"center"}>
                You can now log in to your account
              </Text>
              <Button variant="link" onClick={handleGoHome}>
                Homepage
              </Button>
              <Text as="h3" align={"center"}>
                You will be redirected to home page in 5 seconds...
              </Text>
            </>
          ) : (
            <>
              <VStack gap={5}>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="Nickname"
                    bgColor="bg"
                    onChange={handleSetNickname}
                    value={nickname}
                    disabled={activating}
                    onKeyDown={handleKeyDown}
                  />
                  <InputRightElement>
                    <FA icon={faICursor} />
                  </InputRightElement>
                </InputGroup>
                <InputGroup>
                  <Input
                    type={`${showPassword ? "text" : "password"}`}
                    placeholder="Password"
                    bgColor="bg"
                    onChange={handlePassword}
                    disabled={activating}
                    onKeyDown={handleKeyDown}
                  />
                  <InputRightElement>
                    <FA
                      icon={showPassword ? faEyeSlash : faEye}
                      onClick={togglePasswordLogin}
                    />
                  </InputRightElement>
                </InputGroup>
                <InputGroup>
                  <Input
                    type={`${showPassword ? "text" : "password"}`}
                    placeholder="Confirm password"
                    bgColor="bg"
                    onChange={handleConfirmPassword}
                    disabled={activating}
                    onKeyDown={handleKeyDown}
                  />
                  <InputRightElement>
                    <FA
                      icon={showPassword ? faEyeSlash : faEye}
                      onClick={togglePasswordLogin}
                    />
                  </InputRightElement>
                </InputGroup>
              </VStack>
              <HStack gap="20px">
                <Button onClick={handleActivateAccount} id="activate-btn">
                  {activating ? (
                    <Spinner
                      thickness="4px"
                      speed="0.65s"
                      emptyColor="gray.200"
                      color="brand.500"
                      size="md"
                    />
                  ) : (
                    "Continue"
                  )}
                </Button>
                <Button variant="link" href="/">
                  Already activated?
                </Button>
              </HStack>
            </>
          )}
        </VStack>
      </Flex>
    </DefaultLayout>
  );
};

export const Login = ({ showLogin, setShowLogin }) => {
  const { loggedIn, userInfo, doLogout } = useContext(GlobalContext);
  const [login, setLogin] = useState(true);

  return (
    <>
      <Drawer
        isOpen={showLogin}
        onClose={() => {
          setShowLogin(false);
          setLogin(true);
        }}
        placement="right"
        size={loggedIn ? "sm" : "md"}
      >
        <DrawerOverlay />
        <DrawerContent borderRadius={"15px 0px 0px 15px"} bgColor="bg">
          <DrawerHeader>
            <DrawerCloseButton top={5} left={6} />
          </DrawerHeader>
          <DrawerBody>
            <MainContent>
              {loggedIn ? (
                <LoggedIn userInfo={userInfo} doLogout={doLogout} />
              ) : login ? (
                <LoginForm setSignUp={setLogin} />
              ) : (
                <SignupForm setLogin={setLogin} />
              )}
            </MainContent>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
